import {
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Box,
  createTheme,
  ThemeProvider,
  Typography,
  SelectChangeEvent,
  Fade,
  FormControlLabel,
  Switch,
  FormControl as MuiFormControl,
  ButtonBase,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Chip from "@mui/material/Chip";
import {
  Clear as ClearIcon,
  FilterList as FilterListIcon,
} from "@mui/icons-material";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Button from "@mui/material/Button";
import { ProgramFilterDialog } from "src/components/dialog/ProgramFilterDialog";
import {
  Hcp,
  ProgramFiltersWithCountrySpecificFilters,
} from "src/types/programFilters";
import AffiliateService from "src/services/affiliate.service";
import { ProgramDetail } from "src/types/affiliatePrograms";
import { AuthContext } from "src/utils/authContext";
import Loader from "src/components/loader/loader";
import { AffiliateProgramsTable } from "src/components/affiliate/AffiliateProgramsTable";
import MainFormPage from "./formPage";
import { theme } from "src/App";

// 1. Theme Configuration
const customTheme = createTheme({
  palette: {
    primary: {
      main: "#5c6bc0", // Indigo
    },
    secondary: {
      main: "#4db6ac", // Teal
    },
    background: {
      default: "#f4f6f8",
    },
  },
  components: {
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 3,
      },
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        list: {
          // Fix for the double scrollbar issue by ensuring only one element manages overflow
          paddingTop: 0,
          paddingBottom: 0,
          maxHeight: 320, // Set max height on the list itself
          overflowY: "auto",
        },
      },
    },
  },
});

const { getCtvQuarters, getCountries, getHcps, getData, getProgramNumbers } =
  AffiliateService();

interface StatusContainerProps {
  active?: boolean;
}

// Styled container typed with StatusContainerProps
const StatusContainer = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "active",
})<StatusContainerProps>(({ theme, active }) => ({
  padding: theme.spacing(1, 2),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${active ? "#4caf50" : "#d1d1d1"}`,
  backgroundColor: active ? "#fafffa" : "transparent",
  transition: "all 0.2s ease-in-out",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: theme.spacing(2),
  boxShadow: "none", // Remove all shadows
}));

const ControlRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
});

export const AffiliatePage = () => {
  const { checkAuthentication, user } = useContext(AuthContext);
  const [loading, setLoading] = useState<boolean>(true);
  const [quarters, setQuarters] = useState<string[]>([]);
  const [countries, setCountries] = useState([]);
  const [hcps, setHcps] = useState<Hcp[]>([]);
  const [programNumbers, setProgramNumbers] = useState<string[]>([]);
  const [programs, setPrograms] = useState<ProgramDetail[]>([]);
  const page = useRef(0);
  const [hcpsForCountry, setHcpsForCountry] = useState(hcps);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(false);

  // Filter selections
  const [selectedQuarters, setSelectedQuarters] = useState<string[]>([]);
  const [selectedHcps, setSelectedHcps] = useState<string[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedProgramNumbers, setSelectedProgramNumbers] = useState<
    string[]
  >([]);

  // CTV Submission states
  const [isSubmitMode, setIsSubmitMode] = useState<boolean>(false);
  const [selectedHcpForSub, setSelectedHcpForSub] = useState<string>("");
  const [selectedCountryForCtvSubmission, setSelectedCountryForCtvSubmission] =
    useState<string>("");

  // View mode toggle
  const handleToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsSubmitMode(event.target.checked);
  };

  const handleTypeChange = (event: SelectChangeEvent) => {
    setSelectedHcpForSub(event.target.value as string);
  };

  const handleCountryChange = (event: SelectChangeEvent) => {
    setSelectedCountryForCtvSubmission(event.target.value as string);
    setSelectedHcpForSub("");
  };

  const authorizedHcps = useMemo(
    () => hcps.filter((hcp) => hcp.country == selectedCountryForCtvSubmission),
    [hcps, selectedCountryForCtvSubmission],
  );

  const isAffilateAuthorizedToSubmit = useMemo(() => {
    return hcps.some((hcp) => hcp.affiliateAuthorized);
  }, [hcps]);

  // Dialog
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Memo
  // Combine all selected IDs into a single array for rendering chips
  const allSelectedFilters = useMemo(
    () => [
      ...selectedQuarters,
      ...selectedCountries,
      ...selectedHcps,
      ...selectedProgramNumbers,
    ],
    [selectedQuarters, selectedHcps, selectedCountries, selectedProgramNumbers],
  );

  // Unified map of ID to its setter function for dynamic removal
  const setterMap = useMemo(
    () =>
      new Map<string, React.Dispatch<React.SetStateAction<string[]>>>([
        ...selectedQuarters.map((item) => [item, setSelectedQuarters] as const),
        ...selectedCountries.map(
          (item) => [item, setSelectedCountries] as const,
        ),
        ...selectedHcps.map((item) => [item, setSelectedHcps] as const),
        ...selectedProgramNumbers.map(
          (item) => [item, setSelectedProgramNumbers] as const,
        ),
      ]),
    [selectedQuarters, selectedHcps, selectedCountries, selectedProgramNumbers],
  );

  const handleRemovePill = useCallback(
    (idToRemove: string) => {
      // Find the correct setter function for the item being removed
      const setter = setterMap.get(idToRemove);
      if (setter) {
        setter((prevIds) => prevIds.filter((id) => id !== idToRemove));
      }
    },
    [setterMap],
  );

  // Clear All Filters
  const handleClearAllFilters = useCallback(() => {
    setSelectedQuarters([]);
    setSelectedHcps([]);
    setSelectedCountries([]);
    setSelectedProgramNumbers([]);
  }, []);

  const handleFilterSelection = ({
    quarters,
    countries,
    hcps,
    hcpsForCountry,
    programNumbers,
  }: ProgramFiltersWithCountrySpecificFilters) => {
    setSelectedCountries(countries);
    setSelectedHcps(hcps);
    setSelectedQuarters(quarters);
    setHcpsForCountry(hcpsForCountry);
    setSelectedProgramNumbers(programNumbers);
    setIsDialogOpen(false);
  };

  const getInitialData = async () => {
    setLoading(true);
    page.current = 0;
    const programs = await getData({
      page: 0,
      ...(selectedCountries.length > 0 && {
        countries: selectedCountries.join(","),
      }),
      ...(selectedQuarters.length > 0 && {
        ctvquarters: selectedQuarters.join(","),
      }),
      ...(selectedHcps.length > 0 && { hcps: selectedHcps.join(",") }),
      ...(selectedProgramNumbers.length > 0 && {
        programnumbers: selectedProgramNumbers.join(","),
      }),
    });

    setPrograms(programs.hcpProgramDetails);
    setHasMore(programs.has_more);
    setLoading(false);
  };

  const getFilters = async () => {
    setLoadingFilters(true);
    const [ctvQuarters, countries, hcps, programNumbers] = await Promise.all([
      getCtvQuarters(),
      getCountries(),
      getHcps(),
      getProgramNumbers(),
    ]);
    setQuarters(ctvQuarters.ctvQuartersList);
    setCountries(countries.countriesList);
    setHcps(hcps.hcpsList);
    setProgramNumbers(programNumbers.programNumbersList);
    setSelectedCountryForCtvSubmission(countries?.countriesList[0]);
    setLoadingFilters(false);
  };

  const loadMore = async () => {
    if (loading || loadingMore) {
      return;
    }

    setLoadingMore(true);
    const programs = await getData({
      page: ++page.current,
      ...(selectedCountries.length > 0 && {
        countries: selectedCountries.join(","),
      }),
      ...(selectedQuarters.length > 0 && {
        ctvquarters: selectedQuarters.join(","),
      }),
      ...(selectedHcps.length > 0 && { hcps: selectedHcps.join(",") }),
      ...(selectedProgramNumbers.length > 0 && {
        programnumbers: selectedProgramNumbers.join(","),
      }),
    });
    setPrograms((prev) => [...prev, ...programs.hcpProgramDetails]);
    setHasMore(programs.has_more);
    setLoadingMore(false);
  };

  useEffect(() => {
    getFilters();
  }, []);

  useEffect(() => {
    getInitialData();
  }, [JSON.stringify(allSelectedFilters)]);

  useEffect(() => {
    if (selectedCountries.length > 0) {
      setSelectedHcps(
        hcpsForCountry
          .filter((hcp) => selectedHcps.includes(hcp.name))
          .map((hcp) => hcp.name),
      );
    }
  }, [hcpsForCountry]);

  useEffect(() => {
    if (selectedCountries.length > 0) {
      setHcpsForCountry(
        hcps.filter((hcp) => selectedCountries.includes(hcp.country)),
      );
    } else {
      setHcpsForCountry(hcps);
    }
  }, [selectedCountries]);

  return (
    <ThemeProvider theme={customTheme}>
      {isDialogOpen && (
        <ProgramFilterDialog
          countries={countries}
          hcps={hcps}
          quarters={quarters}
          programNumbers={programNumbers}
          previousSelectedCountries={selectedCountries}
          previousSelectedHcps={selectedHcps}
          previousSelectedQuarters={selectedQuarters}
          previousSelectedProgramNumbers={selectedProgramNumbers}
          open={isDialogOpen}
          onApply={handleFilterSelection}
          onCancel={() => {
            setIsDialogOpen(false);
          }}
        />
      )}
      <div className="wrapper">
        {/* MUI TOGGLE CONTROLLER - Segmented Style with sliding dynamic highlight */}
        {hcps?.length > 0 && isAffilateAuthorizedToSubmit && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                position: "relative",
                display: "flex",
                bgcolor: "#f1f5f9",
                p: 0.75,
                borderRadius: 4,
                width: { xs: "100%", sm: 300 },
                height: 48,
                alignItems: "center",
                boxShadow: "inset 0 2px 4px rgba(0,0,0,0.02)",
              }}
            >
              {/* Sliding Highlight Background Layer */}
              <Box
                sx={{
                  position: "absolute",
                  top: 6,
                  bottom: 6,
                  left: !isSubmitMode ? "6px" : "calc(50%)",
                  width: "calc(50% - 6px)",
                  bgcolor: "background.paper",
                  borderRadius: 3,
                  boxShadow:
                    "0 4px 6px -1px rgba(0,0,0,0.08), 0 2px 4px -1px rgba(0,0,0,0.04)",
                  transition: "left 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  zIndex: 0,
                }}
              />

              {/* View Select Buttons */}
              <ButtonBase
                onClick={() => setIsSubmitMode(false)}
                sx={{
                  position: "relative",
                  zIndex: 1,
                  flex: 1,
                  height: "100%",
                  borderRadius: 3,
                  fontSize: "0.85rem",
                  fontWeight: "bold",
                  color: !isSubmitMode ? "primary.main" : "text.secondary",
                  transition: "color 0.2s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                Safety Reports Summary
              </ButtonBase>

              <ButtonBase
                onClick={() => setIsSubmitMode(true)}
                sx={{
                  position: "relative",
                  zIndex: 1,
                  flex: 1,
                  height: "100%",
                  borderRadius: 3,
                  fontSize: "0.85rem",
                  fontWeight: "bold",
                  color: isSubmitMode ? "primary.main" : "text.secondary",
                  transition: "color 0.2s ease",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                }}
              >
                Roche CTV Completion
              </ButtonBase>
            </Box>
            {isSubmitMode && (
              <Box>
                <StatusContainer elevation={0} active={isSubmitMode}>
                  <ControlRow>
                    {/* Toggle Switch */}
                    {/* <FormControlLabel
                      sx={{ m: 0 }}
                      control={
                        <Switch
                          size="small"
                          checked={isSubmitMode}
                          onChange={handleToggle}
                          color="success"
                        />
                      }
                      label={
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 500,
                            color: "#333",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Submit For:
                        </Typography>
                      }
                    /> */}
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: "#333",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Submit For:
                    </Typography>

                    {/* Dropdown - Only visible when active */}
                    {isSubmitMode && (
                      <>
                        <MuiFormControl sx={{ minWidth: 140 }} size="small">
                          <InputLabel id="submit-country">Country</InputLabel>
                          <Select
                            labelId="submit-country"
                            id="submit-country-select"
                            value={selectedCountryForCtvSubmission}
                            label="Select Option"
                            onChange={handleCountryChange}
                            sx={{ height: "32px", fontSize: "0.875rem" }}
                            disabled={countries.length === 1}
                          >
                            {countries.map((country) => (
                              <MenuItem key={country} value={country}>
                                {country}
                              </MenuItem>
                            ))}
                          </Select>
                        </MuiFormControl>
                        <Fade in={isSubmitMode}>
                          <MuiFormControl sx={{ minWidth: 140 }} size="small">
                            <InputLabel id="submit-type-label">
                              Select HCP
                            </InputLabel>
                            <Select
                              labelId="submit-type-label"
                              id="submit-type-select"
                              value={selectedHcpForSub}
                              label="Select Option"
                              onChange={handleTypeChange}
                              sx={{ height: "32px", fontSize: "0.875rem" }}
                            >
                              {authorizedHcps.map((hcp) => (
                                <MenuItem key={hcp.email} value={hcp.email}>
                                  {hcp.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </MuiFormControl>
                        </Fade>
                      </>
                    )}
                  </ControlRow>
                </StatusContainer>
              </Box>
            )}
          </Box>
        )}
        {/* Dropdown and Button container */}
        <Box
          sx={{
            position: "relative",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexWrap: "wrap",
            mb: { xs: 2, lg: 0 },
            mr: { lg: 2 },
            width: "100vw",
            marginTop: 2,
          }}
        >
          {!isSubmitMode && (
            <Button
              aria-controls={isDialogOpen ? "filter-menu-" : undefined}
              aria-haspopup="true"
              aria-expanded={isDialogOpen ? "true" : undefined}
              onClick={() => setIsDialogOpen(true)}
              variant={allSelectedFilters.length > 0 ? "contained" : "outlined"}
              color="primary"
              startIcon={<FilterListIcon />}
              sx={{
                position: "absolute",
                left: 0,
                borderRadius: 2,
                textTransform: "none",
                ...(allSelectedFilters.length > 0 && {
                  bgcolor: "#5c6bc0",
                  "&:hover": { bgcolor: "#4e5baa" },
                }),
                ...(loadingFilters && {
                  bgcolor: "#adadadff",
                  "&:hover": { bgcolor: "#adadadff" },
                }),
              }}
              disabled={loadingFilters}
            >
              Open Filter
            </Button>
          )}

          <Typography variant="h4">
            {isSubmitMode ? `CTV List for completion` : `List of CTVs`}
          </Typography>
        </Box>

        {/* Applied Filters / Pills Display */}
        {!isSubmitMode && (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              mt: { xs: 2, lg: 1 },
            }}
          >
            {allSelectedFilters.map((item) => (
              <Chip
                key={item}
                label={item}
                onDelete={() => handleRemovePill(item)}
                color="primary"
                variant="filled"
                size="medium"
                deleteIcon={<ClearIcon />}
                sx={{
                  bgcolor: "secondary.main",
                  color: "white",
                  "& .MuiChip-deleteIcon": { color: "white" },
                }}
              />
            ))}

            {allSelectedFilters.length > 0 && (
              <Button
                onClick={handleClearAllFilters}
                variant="outlined"
                color="error"
                size="small"
                startIcon={<ClearIcon />}
                sx={{ textTransform: "none", ml: { xs: 0, md: 1 } }}
              >
                Clear All ({allSelectedFilters.length})
              </Button>
            )}
          </Box>
        )}

        {/* Loading Message with smooth transition */}
        {loading ? (
          <div className="loading-container fade-in">
            <Loader />
          </div>
        ) : isSubmitMode ? (
          selectedHcpForSub ? (
            <ThemeProvider theme={theme}>
              <MainFormPage
                key={selectedHcpForSub}
                affiliateMode={true}
                email={selectedHcpForSub}
              />
            </ThemeProvider>
          ) : (
            <Typography variant="h5" align="center">
              Select an HCP for Submission
            </Typography>
          )
        ) : (
          <AffiliateProgramsTable
            programs={programs}
            hasMore={hasMore}
            loadMore={loadMore}
            loadingMore={loadingMore}
          />
        )}
      </div>
    </ThemeProvider>
  );
};
